/**
 * Simple WebSocket client with subscription registry for real-time updates
 */

const WS_URL = process.env.REACT_APP_WS_URL || '';

class RealtimeClient {
  constructor() {
    this.ws = null;
    this.connected = false;
    this.queue = [];
    this.subscribers = new Map(); // topic -> Set<fn>
    this.reconnectDelay = 1000;
    this.maxReconnectDelay = 15000;
  }

  connect() {
    if (!WS_URL) return; // No WS configured
    if (this.ws) return;
    try {
      this.ws = new WebSocket(WS_URL);
      this.ws.onopen = () => {
        this.connected = true;
        this.reconnectDelay = 1000;
        // Flush queue of messages to send
        while (this.queue.length) {
          this.ws.send(this.queue.shift());
        }
      };
      this.ws.onmessage = (evt) => this.handleMessage(evt);
      this.ws.onclose = () => this.handleClose();
      this.ws.onerror = () => this.handleClose();
    } catch (e) {
      this.handleClose();
    }
  }

  handleMessage(evt) {
    let msg;
    try {
      msg = JSON.parse(evt.data);
    } catch (e) {
      return;
    }
    const { type, topic, event, payload } = msg;
    const key = topic || type || event;
    if (!key) return;
    const subs = this.subscribers.get(key);
    if (subs) {
      subs.forEach((fn) => {
        try {
          fn(payload || msg);
        } catch (e) {
          // ignore subscriber errors
        }
      });
    }
  }

  handleClose() {
    this.connected = false;
    if (this.ws) {
      try {
        this.ws.close();
      } catch (_) {}
    }
    this.ws = null;
    // Exponential backoff reconnect
    setTimeout(() => this.connect(), this.reconnectDelay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
  }

  send(obj) {
    const message = JSON.stringify(obj);
    if (this.connected && this.ws) this.ws.send(message);
    else this.queue.push(message);
  }

  /**
   * Subscribe to a topic; returns unsubscribe function
   * Common topics: inventory.update, inventory.alert, production.plan.update, production.run.update
   * @param {string} topic
   * @param {(payload:any)=>void} fn
   * @returns {() => void}
   */
  subscribe(topic, fn) {
    if (!this.subscribers.has(topic)) this.subscribers.set(topic, new Set());
    this.subscribers.get(topic).add(fn);
    this.connect();
    return () => {
      const set = this.subscribers.get(topic);
      if (!set) return;
      set.delete(fn);
      if (set.size === 0) this.subscribers.delete(topic);
    };
  }
}

const realtime = new RealtimeClient();
export default realtime;